import { Box, Checkbox, Typography } from '@mui/material'
import { Deposit } from '../../api/types'
import { QueueColumn, QueueTable } from '../../components/QueueTable'
import { formatCzk, formatDateTime } from './format'

interface PaidQueueCardProps {
  /** Klíč fondu — remount QueueTable při přepnutí (jinak drží staré řazení/hledání). */
  fundKey: string
  deposits: Deposit[]
  selected: Set<string>
  onToggle: (depositId: string) => void
  onToggleAll: (checked: boolean) => void
}

/**
 * Fronta zaplacených vkladů fondu čekajících na přidělení. Výběr účastníků drží
 * rodič (lift state) — tabulka je per fond, řazená nejstarší nahoře (fronta).
 */
export function PaidQueueCard({
  fundKey,
  deposits,
  selected,
  onToggle,
  onToggleAll,
}: PaidQueueCardProps) {
  const selectedCount = deposits.filter((d) => selected.has(d.id)).length
  const allSelected = deposits.length > 0 && selectedCount === deposits.length
  const someSelected = selectedCount > 0 && selectedCount < deposits.length

  const columns: QueueColumn<Deposit>[] = [
    {
      key: 'select',
      label: '',
      render: (d) => (
        <Checkbox
          size="small"
          checked={selected.has(d.id)}
          onChange={() => onToggle(d.id)}
        />
      ),
    },
    {
      key: 'client',
      label: 'Klient',
      getSortValue: (d) => d.clientName,
      render: (d) => d.clientName,
    },
    {
      key: 'amount',
      label: 'Vklad',
      align: 'right',
      getSortValue: (d) => d.amountReceived ?? d.amount,
      render: (d) => {
        const received = d.amountReceived ?? d.amount
        return (
          <Box>
            <Typography variant="body2">{formatCzk(received)}</Typography>
            {d.amountReceived != null && d.amountReceived !== d.amount && (
              <Typography variant="caption" color="text.secondary" component="div">
                sjednáno {formatCzk(d.amount)}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      key: 'paidAt',
      label: 'Potvrzeno',
      getSortValue: (d) => d.paidAt ?? '',
      render: (d) => (
        <Box>
          <Typography variant="body2">{d.paidAt ? formatDateTime(d.paidAt) : '—'}</Typography>
          {d.paidBy && (
            <Typography variant="caption" color="text.secondary" component="div">
              {d.paidBy}
            </Typography>
          )}
        </Box>
      ),
    },
  ]

  const toolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Checkbox
        size="small"
        checked={allSelected}
        indeterminate={someSelected}
        onChange={(e) => onToggleAll(e.target.checked)}
        disabled={deposits.length === 0}
      />
      <Typography variant="body2" color="text.secondary">
        {selectedCount > 0 ? `vybráno ${selectedCount}` : 'Vybrat vše'}
      </Typography>
    </Box>
  )

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Fronta zaplacených vkladů (řazeno dle času potvrzení)
      </Typography>
      <QueueTable
        key={fundKey}
        columns={columns}
        rows={deposits}
        getRowKey={(d) => d.id}
        getSearchText={(d) => d.clientName}
        searchPlaceholder="Hledat klienta…"
        defaultSortKey="paidAt"
        defaultSortDir="asc"
        emptyText="Žádné zaplacené vklady čekající na přidělení"
        toolbar={toolbar}
      />
    </Box>
  )
}
